import semver from 'semver'
import { useEffect, useMemo, useRef } from 'react'
import useRoomContextRef from './useRoomContextRef'
import useRoomActions from './useRoomActions'
import { logger } from './Logger'
import useUserContextRef from './useUserContextRef'
import { MusePeer } from './RoomContextMusePeer'

const log = logger.createChildLogger({ name: 'PeerPingMeasurement' })

type PingsAwaitingReply = {
    id: number
    timeSent: number
    receiverUserUrn: string
    sessionId: string
    username: string
}

export type HostPingMeasurement = {
    sessionId: string
    username: string
    roundTripLatencyMs: number
}

export default function usePeerPingMeasurement() {
    const { roomStateRef } = useRoomContextRef()
    const { userStateRef } = useUserContextRef()

    const { sendMessageToPeer } = useRoomActions()

    const nextId = useRef<number>(0)
    const pingAwaitingReplyRef = useRef<PingsAwaitingReply>()
    const latestHostPingMeasurement = useRef<HostPingMeasurement>()

    useEffect(() => {
        let timer = setInterval(() => {
            const myUserUrn = userStateRef.current.userUrn
            const hostUrn = roomStateRef.current.host?.userUrn

            if (myUserUrn === hostUrn) {
                return
            }

            let host: MusePeer | undefined
            Object.keys(roomStateRef.current.peers).forEach((sessionId) => {
                const peer = roomStateRef.current.peers[sessionId]
                if (peer.userUrn === hostUrn) {
                    host = peer
                }
            })

            if (host) {
                if (host.appVersion && semver.gte(host.appVersion, '1.5.10')) {
                    pingAwaitingReplyRef.current = {
                        id: nextId.current,
                        timeSent: Date.now(),
                        receiverUserUrn: host.userUrn,
                        sessionId: host.sessionId,
                        username: host.username,
                    }

                    sendMessageToPeer(host.sessionId, {
                        type: 'ping',
                        data: { id: nextId.current },
                    })

                    nextId.current += 1
                }
            }
        }, 10 * 1000)

        return () => {
            clearInterval(timer)
        }
    }, [])

    return useMemo(() => {
        function onReceivedPingFromPeer(peerSessionId: string, data: any) {
            sendMessageToPeer(peerSessionId, {
                type: 'pong',
                data: data,
            })
        }

        function onReceivedPongFromPeer(peerSessionId: string, data: any) {
            try {
                const { id } = data
                const t1 = Date.now()

                const pingAwaitingReply = pingAwaitingReplyRef.current
                if (!pingAwaitingReply) {
                    return
                }

                if (pingAwaitingReply.id === id && pingAwaitingReply.sessionId === peerSessionId) {
                    const t0 = pingAwaitingReply.timeSent
                    const dt = t1 - t0

                    latestHostPingMeasurement.current = {
                        sessionId: pingAwaitingReply.sessionId,
                        username: pingAwaitingReply.username,
                        roundTripLatencyMs: dt,
                    }
                }
            } catch (e) {
                log.assert('onReceivedPongFromPeer', `An exception occurred!`, { e })
            }
        }

        function getLatestHostPingMeasurement(): HostPingMeasurement | null {
            let t = latestHostPingMeasurement.current
            if (t) {
                return {
                    sessionId: t.sessionId,
                    username: t.username || '',
                    roundTripLatencyMs: t.roundTripLatencyMs,
                }
            }

            return null
        }

        return {
            onReceivedPingFromPeer,
            onReceivedPongFromPeer,
            getLatestHostPingMeasurement,
        }
    }, [])
}
